import { ChevronUpDownIcon, CheckIcon, BuildingStorefrontIcon } from '@heroicons/react/16/solid'
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem, DropdownLabel } from '@/components/ui'
import type { MerchantSummary } from '@/types'

/**
 * Switches the active merchant for users assigned to more than one. Each
 * merchant uses its own payment gateway credentials (no partner-level access),
 * so switching changes which credentials the gateway client uses.
 *
 * Phase 1 renders the chrome; `onSelect` is wired to the server-side switch
 * route in Phase 2. With one (or zero) merchants there is nothing to switch,
 * so it renders a static label.
 */
export function MerchantSwitcher({
  merchants,
  current,
  emptyLabel,
  onSelect,
}: {
  merchants: MerchantSummary[]
  current: MerchantSummary | null
  /** Shown when no merchant is selected (localized by the caller). */
  emptyLabel: string
  onSelect: (merchantId: string) => void
}) {
  const label = current?.name ?? emptyLabel

  if (merchants.length <= 1) {
    return (
      <div className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium text-white/90">
        <BuildingStorefrontIcon aria-hidden="true" className="size-4 shrink-0 text-brand-turquoise" />
        <span className="truncate">{label}</span>
      </div>
    )
  }

  return (
    <Dropdown>
      <DropdownButton
        as="button"
        className="flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-left text-sm font-medium text-white transition-colors hover:bg-white/5"
      >
        <BuildingStorefrontIcon aria-hidden="true" className="size-4 shrink-0 text-brand-turquoise" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronUpDownIcon aria-hidden="true" className="size-4 shrink-0 text-white/60" />
      </DropdownButton>
      <DropdownMenu className="min-w-64" anchor="bottom start">
        {merchants.map((merchant) => (
          <DropdownItem key={merchant.id} onClick={() => onSelect(merchant.id)}>
            <DropdownLabel>{merchant.name}</DropdownLabel>
            {merchant.id === current?.id && <CheckIcon aria-hidden="true" data-slot="icon" />}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  )
}
